import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [constitutionDropdownOpen, setConstitutionDropdownOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isActive = (path: string) => location.pathname === path;

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setConstitutionDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setConstitutionDropdownOpen(false);
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-sm z-50">
      <div className="max-w-7xl mx-auto px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <img 
              src="https://static.readdy.ai/image/87917d4c1997ec0ae278ae84984ea976/1ea3e812b36acb9a2c88266f08973400.jpeg" 
              alt="NZ Constitution Logo" 
              className="h-12 w-12 object-contain"
            />
            <span className="text-xl font-semibold text-[#1B3B6F]" style={{ fontFamily: 'Georgia, serif' }}>
              NZ Constitution 2.0
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-base font-medium transition-colors cursor-pointer ${
                isActive('/') ? 'text-[#1B3B6F]' : 'text-gray-600 hover:text-[#1B3B6F]'
              }`}
            >
              Home
            </Link>

            <Link
              to="/about"
              className={`text-base font-medium transition-colors cursor-pointer ${
                isActive('/about') ? 'text-[#1B3B6F]' : 'text-gray-600 hover:text-[#1B3B6F]'
              }`}
            >
              About
            </Link>

            {/* Constitution Dropdown */}
            <div
              ref={dropdownRef}
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <Link
                to="/constitution"
                className={`text-base font-medium transition-colors cursor-pointer flex items-center gap-1 ${
                  isActive('/constitution') ? 'text-[#1B3B6F]' : 'text-gray-600 hover:text-[#1B3B6F]'
                }`}
              >
                The Constitution
                <i className="ri-arrow-down-s-line w-5 h-5 flex items-center justify-center"></i>
              </Link>

              {constitutionDropdownOpen && (
                <div 
                  className="absolute top-full left-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 py-4"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <div className="px-4 pb-3 border-b border-gray-200">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Section One
                    </div>
                    <Link
                      to="/constitution?subsection=establishment&section=1"
                      className="block py-2 px-3 text-sm text-gray-700 hover:bg-[#F0F5FA] rounded-lg cursor-pointer"
                    >
                      Establishment Article
                    </Link>
                    <Link
                      to="/constitution?subsection=foundation&section=1"
                      className="block py-2 px-3 text-sm text-gray-700 hover:bg-[#F0F5FA] rounded-lg cursor-pointer"
                    >
                      The Foundation Articles
                    </Link>
                    <Link
                      to="/constitution?subsection=rights&section=1"
                      className="block py-2 px-3 text-sm text-gray-700 hover:bg-[#F0F5FA] rounded-lg cursor-pointer"
                    >
                      Personal Rights and Responsibilities
                    </Link>
                    <Link
                      to="/constitution?subsection=cultural&section=1"
                      className="block py-2 px-3 text-sm text-gray-700 hover:bg-[#F0F5FA] rounded-lg cursor-pointer"
                    >
                      Cultural Protections and Safeguards
                    </Link>
                  </div>
                  <div className="px-4 pt-3">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Section Two
                    </div>
                    <Link
                      to="/constitution?subsection=govestablishment&section=2"
                      className="block py-2 px-3 text-sm text-gray-700 hover:bg-[#F0F5FA] rounded-lg cursor-pointer"
                    >
                      Government Establishment
                    </Link>
                    <Link
                      to="/constitution?subsection=structure&section=2"
                      className="block py-2 px-3 text-sm text-gray-700 hover:bg-[#F0F5FA] rounded-lg cursor-pointer"
                    >
                      Structure of Government
                    </Link>
                    <Link
                      to="/constitution?subsection=election&section=2"
                      className="block py-2 px-3 text-sm text-gray-700 hover:bg-[#F0F5FA] rounded-lg cursor-pointer"
                    >
                      Election
                    </Link>
                    <Link
                      to="/constitution?subsection=responsibilities&section=2"
                      className="block py-2 px-3 text-sm text-gray-700 hover:bg-[#F0F5FA] rounded-lg cursor-pointer"
                    >
                      Government Responsibilities
                    </Link>
                    <Link
                      to="/constitution?subsection=principles&section=2"
                      className="block py-2 px-3 text-sm text-gray-700 hover:bg-[#F0F5FA] rounded-lg cursor-pointer"
                    >
                      Summary of Principles
                    </Link>
                    <Link
                      to="/constitution?subsection=closing&section=2"
                      className="block py-2 px-3 text-sm text-gray-700 hover:bg-[#F0F5FA] rounded-lg cursor-pointer"
                    >
                      Closing Article
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/donate"
              className={`text-base font-medium transition-colors cursor-pointer ${
                isActive('/donate') ? 'text-[#1B3B6F]' : 'text-gray-600 hover:text-[#1B3B6F]'
              }`}
            >
              Donate
            </Link>

            <Link
              to="/contact"
              className={`text-base font-medium transition-colors cursor-pointer ${
                isActive('/contact') ? 'text-[#1B3B6F]' : 'text-gray-600 hover:text-[#1B3B6F]'
              }`}
            >
              Contact
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-gray-600 hover:text-[#1B3B6F] cursor-pointer"
          >
            <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-2xl w-6 h-6 flex items-center justify-center`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="block py-3 text-base font-medium text-gray-600 hover:text-[#1B3B6F] cursor-pointer"
            >
              Home
            </Link>
            <Link
              to="/about"
              onClick={() => setMobileMenuOpen(false)}
              className="block py-3 text-base font-medium text-gray-600 hover:text-[#1B3B6F] cursor-pointer"
            >
              About
            </Link>
            <Link
              to="/constitution"
              onClick={() => setMobileMenuOpen(false)}
              className="block py-3 text-base font-medium text-gray-600 hover:text-[#1B3B6F] cursor-pointer"
            >
              The Constitution
            </Link>
            <Link
              to="/donate"
              onClick={() => setMobileMenuOpen(false)}
              className="block py-3 text-base font-medium text-gray-600 hover:text-[#1B3B6F] cursor-pointer"
            >
              Donate
            </Link>
            <Link
              to="/contact"
              onClick={() => setMobileMenuOpen(false)}
              className="block py-3 text-base font-medium text-gray-600 hover:text-[#1B3B6F] cursor-pointer"
            >
              Contact
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
}
